import React, {
    createContext,
    useContext,
    useLayoutEffect,
    useMemo,
    useRef,
    useSyncExternalStore,
} from "react";
import { ISettingsProps } from "../Models.ts";
import { SettingsContext } from "./SettingsContext.tsx";

/**
 * Settings, read one slice at a time (ADR 0081).
 *
 * `SettingsContext` hands every reader the whole settings object, so a change to
 * any setting re-renders every control that reads it, and the PrimeReact ones
 * cost a prop merge each. That left a setting change redrawing ~40 controls to
 * update one. A control that reads through `useSetting` re-renders only when
 * the slice it asked for changes.
 *
 * `SettingsProvider` is the one place a page supplies settings. It keeps
 * providing `SettingsContext` as before, so a component not yet moved over reads
 * it unchanged, and it adds a store whose identity never changes, so reading it
 * never causes a render by itself.
 *
 * A slice is compared by value (its JSON), not by reference. The panel's
 * handlers copy settings shallowly and edit a nested range in place, so a
 * reference check would miss a change to an object that kept its identity. A
 * slice that changed is handed out as a fresh copy for the same reason.
 */
interface SettingsStore {
    value: {
        settings: ISettingsProps;
        setSettings: (settings: ISettingsProps) => void;
    };
    listeners: Set<() => void>;
    subscribe: (listener: () => void) => () => void;
    getSettings: () => ISettingsProps;
    setSettings: (settings: ISettingsProps) => void;
}

const SettingsStoreContext = createContext<SettingsStore | null>(null);

function createStore(value: SettingsStore['value']): SettingsStore {
    const store: SettingsStore = {
        value,
        listeners: new Set(),
        subscribe(listener) {
            store.listeners.add(listener);
            return () => {
                store.listeners.delete(listener);
            };
        },
        getSettings: () => store.value.settings,
        setSettings: (settings) => store.value.setSettings(settings),
    };
    return store;
}

export function SettingsProvider({
    settings,
    setSettings,
    seed,
    setSeed,
    children,
}: {
    settings: ISettingsProps;
    setSettings: (settings: ISettingsProps) => void;
    seed?: string;
    setSeed?: (seed: string) => void;
    children: React.ReactNode;
}) {
    // The value `SettingsContext` has always carried. Memoised so that a render
    // which changes no setting leaves it alone (ADR 0074 §2).
    const contextValue = useMemo(
        () => ({ settings, setSettings, seed, setSeed }),
        [settings, setSettings, seed, setSeed]
    );
    const storeRef = useRef<SettingsStore | null>(null);
    if (storeRef.current === null) {
        storeRef.current = createStore(contextValue);
    }
    const store = storeRef.current;
    // Written during render, not in an effect, so a control mounted by this very
    // render reads the settings this render is for. It is idempotent.
    store.value = contextValue;
    // Told after the commit, once every control that is going to re-render
    // because its parent did has done so. Each listener then re-reads its slice
    // and renders again only if that slice differs.
    useLayoutEffect(() => {
        store.listeners.forEach((listener) => listener());
    }, [store, settings]);

    return (
        <SettingsStoreContext.Provider value={store}>
            <SettingsContext.Provider value={contextValue}>{children}</SettingsContext.Provider>
        </SettingsStoreContext.Provider>
    );
}

function useSettingsStore(): SettingsStore {
    const store = useContext(SettingsStoreContext);
    if (store === null) {
        throw new Error('useSetting needs a <SettingsProvider> above it.');
    }
    return store;
}

/**
 * The slice of settings `select` returns. The component re-renders when that
 * slice changes and not otherwise.
 *
 * `select` must be a pure function of the settings, and what it returns must
 * survive `JSON.stringify` (numbers, strings, booleans, arrays and plain
 * objects). Return only what the component renders from: a derived flag
 * (`showsHalfPage(s)`) rather than the object the flag was derived from.
 */
export function useSetting<T>(select: (settings: ISettingsProps) => T): T {
    const store = useSettingsStore();
    const cache = useRef<{ json: string | undefined; value: T } | null>(null);
    const selectRef = useRef(select);
    selectRef.current = select;

    const getSnapshot = (): T => {
        const next = selectRef.current(store.value.settings);
        const json = JSON.stringify(next);
        if (cache.current !== null && cache.current.json === json) {
            return cache.current.value;
        }
        const value = next !== null && typeof next === 'object' ? (JSON.parse(json as string) as T) : next;
        cache.current = { json, value };
        return value;
    };
    return useSyncExternalStore(store.subscribe, getSnapshot, getSnapshot);
}

/**
 * Reading and writing settings from an event handler, which needs the settings
 * as they are when the event happens and not as they were at the last render.
 * Both functions keep their identity for the life of the page.
 */
export function useSettingsActions(): {
    getSettings: () => ISettingsProps;
    setSettings: (settings: ISettingsProps) => void;
} {
    const store = useSettingsStore();
    return store;
}
